import { Controller } from "@hotwired/stimulus"

// Keeps the persistent module sidebar's active-state in sync with the current
// URL. The sidebar lives OUTSIDE the #module_content turbo-frame, so it is
// mounted once and never re-rendered on in-module navigation — which means its
// highlight can't come from server-rendered `params[:controller]` anymore.
// Instead, on every (frame) navigation we derive the active section + item
// purely from window.location:
//
//   - section highlight: each top-level group is a `section` target. The group
//     whose links contain the LONGEST path-prefix of the current URL wins, so
//     index / new / edit / show / deeply-nested document routes all resolve to
//     the right group without any hardcoded controller→group map.
//   - item highlight: the sub-item link whose href EXACTLY matches the current
//     path gets aria-current="page" (styled via CSS).
export default class extends Controller {
  static targets = ["section"]

  connect() {
    this.refresh = this.refresh.bind(this)
    // Only the main content frame navigating changes the active page — ignore
    // nested frames (list tables, search) loading inside it.
    this.onFrameLoad = (event) => {
      if (event.target && event.target.id === "module_content") this.refresh()
    }
    // Re-apply after a turbo_stream updates part of the sidebar (e.g. a CRUD
    // refreshing a section's list frame), since the freshly rendered links
    // don't carry aria-current. Defer a frame so it runs after the DOM swap.
    this.scheduleRefresh = () => requestAnimationFrame(this.refresh)
    document.addEventListener("turbo:load", this.refresh)
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    document.addEventListener("turbo:before-stream-render", this.scheduleRefresh)
    this.refresh()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.refresh)
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    document.removeEventListener("turbo:before-stream-render", this.scheduleRefresh)
  }

  refresh() {
    const path = window.location.pathname

    // Clear stale item highlights before recomputing.
    this.element
      .querySelectorAll('a[aria-current="page"]')
      .forEach((link) => link.removeAttribute("aria-current"))

    // Single longest path-prefix winner across the whole sidebar drives both the
    // item highlight (aria-current) and its owning section's highlight. Using a
    // prefix (not an exact match) keeps the current record highlighted on its
    // new/edit pages too (e.g. /variables/42/edit still resolves to /variables/42).
    let bestLink = null
    let bestSection = null
    let bestLen = -1

    this.sectionTargets.forEach((section) => {
      section.querySelectorAll("a[href]").forEach((link) => {
        // Skip placeholder/script anchors (e.g. href="#" dropdown triggers) —
        // they resolve to the current path and would match every page.
        const raw = link.getAttribute("href")
        if (!raw || raw === "#" || raw.startsWith("#") || raw.startsWith("javascript:")) {
          return
        }
        let linkPath
        try {
          linkPath = new URL(link.href).pathname
        } catch (e) {
          return
        }
        if (path === linkPath || path.startsWith(linkPath + "/")) {
          if (linkPath.length > bestLen) {
            bestLen = linkPath.length
            bestLink = link
            bestSection = section
          }
        }
      })
    })

    if (bestLink) bestLink.setAttribute("aria-current", "page")
    this.sectionTargets.forEach((section) => {
      this.toggleSection(section, section === bestSection)
    })
  }

  toggleSection(section, isActive) {
    const header = section.querySelector(".menu-item")
    if (header) {
      header.classList.toggle("menu-item-active", isActive)
      header.classList.toggle("menu-item-inactive", !isActive)
      const icon = header.querySelector(
        ".menu-item-icon-active, .menu-item-icon-inactive"
      )
      if (icon) {
        icon.classList.toggle("menu-item-icon-active", isActive)
        icon.classList.toggle("menu-item-icon-inactive", !isActive)
      }
    }
  }
}
